// src/pages/dashboard/DashboardPage.tsx

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from "recharts";
import { getDashboardStats } from "../../api/dashboardApi";
import { DashboardStats, PaymentStatus } from "../../types";
import { getErrorMessage } from "../../utils/errors";
import { formatDate, formatMonth, fullName } from "../../utils/format";
import { Alert } from "../../components/ui/Alert";
import {
  AccountBadge,
  PlanBadge,
  PLAN_LABEL,
} from "../../components/ui/Badges";
import UserAvatar from "../../components/ui/UserAvatar";

// Colores de la app para cada estado de la suscripción
const PLAN_COLORS: Record<string, string> = {
  gratuito: "#F58D58",
  pagado: "#22D3AA",
  cupon: "#6E51DC",
  cancelado: "#D60D4C",
  pendiente: "#3564F7",
};

// ── StatCard uniforme ─────────────────────────────────────────────────────────

function StatCard({
  icon,
  label,
  value,
  sub,
  color,
  onClick,
}: {
  icon: string;
  label: string;
  value: string | number;
  sub?: string;
  color: string;
  onClick?: () => void;
}) {
  return (
    <div
      className="stat-card h-100"
      style={{ cursor: onClick ? "pointer" : "default" }}
      onClick={onClick}
    >
      <div className={`stat-icon ${color}`}>
        <i className={`bi ${icon}`} />
      </div>
      <div className="stat-info">
        <div className="stat-label">{label}</div>
        <div className="stat-value">{value}</div>
        {sub && <div className="stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

// ── PÁGINA PRINCIPAL ──────────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setStats(await getDashboardStats());
    } catch (err) {
      setError(getErrorMessage(err, "No se pudieron cargar los indicadores."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !stats) {
    return (
      <div className="loading-state">
        <div className="spinner-border" />
      </div>
    );
  }

  const pieData = stats
    ? Object.entries(stats.subscriptionsByStatus)
        .map(([status, value]) => ({
          name: PLAN_LABEL[status as PaymentStatus] ?? status,
          value,
          color: PLAN_COLORS[status] ?? "#999999",
        }))
        .filter((d) => d.value > 0)
    : [];

  const barData = (stats?.registrationsByMonth ?? []).map((m) => ({
    mes: formatMonth(m.month),
    registros: m.total,
  }));

  return (
    <>
      {/* Page header */}
      <div className="page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h1 className="page-title">
            <i
              className="bi bi-grid-1x2-fill me-2"
              style={{ color: "var(--tp-green)", fontSize: "1.2rem" }}
            />
            Dashboard
          </h1>
          <p className="page-subtitle">
            Resumen de usuarios, suscripciones y cupones de Toinup
          </p>
        </div>
        <button className="btn-tp-outline" onClick={load} disabled={loading}>
          <i className={`bi bi-arrow-clockwise ${loading ? "spin" : ""}`} />
          Actualizar
        </button>
      </div>

      {error && (
        <Alert type="error" message={error} onClose={() => setError("")} />
      )}

      {stats && (
        <>
          {/* ── Stats — 6 tarjetas uniformes en 3 columnas desktop ── */}
          <div className="row g-3 mb-4">
            {[
              {
                icon: "bi-people-fill",
                label: "Usuarios registrados",
                value: stats.totalUsers,
                sub: "Total en Toinup",
                color: "primary",
                onClick: () => navigate("/usuarios"),
              },
              {
                icon: "bi-person-check-fill",
                label: "Cuentas activas",
                value: stats.enabledUsers,
                sub: "Pueden iniciar sesión",
                color: "green",
                onClick: () => navigate("/usuarios?status=ACTIVOS"),
              },
              {
                icon: "bi-person-x-fill",
                label: "Cuentas inactivas",
                value: stats.disabledUsers,
                sub: "Requieren activación",
                color: "red",
                onClick: () => navigate("/usuarios?status=INACTIVOS"),
              },
              {
                icon: "bi-gem",
                label: "Usuarios Premium",
                value: stats.premiumUsers,
                sub: "Pagado o cupón vigente",
                color: "morado",
              },
              {
                icon: "bi-ticket-perforated-fill",
                label: "Cupones activos",
                value: stats.activeCoupons,
                sub: "Vigentes y con usos",
                color: "orange",
                onClick: () => navigate("/cupones?status=ACTIVO"),
              },
              {
                icon: "bi-gift-fill",
                label: "Cupones usados",
                value: stats.couponUses,
                sub: "Aplicados o asignados",
                color: "blue",
                onClick: () => navigate("/cupones"),
              },
            ].map((s) => (
              <div key={s.label} className="col-12 col-sm-6 col-xl-4">
                <StatCard {...s} />
              </div>
            ))}
          </div>

          {/* ── Gráficas ── */}
          <div className="row g-3 mb-4">
            <div className="col-12 col-lg-7">
              <div className="tp-card h-100">
                <div className="tp-card-header">
                  <div className="tp-card-title">Registros por mes</div>
                  <span className="text-muted-tp">Últimos 6 meses</span>
                </div>
                <div className="tp-card-body" style={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={barData}
                      margin={{ top: 8, right: 8, left: -18, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#e6ecf8"
                      />
                      <XAxis
                        dataKey="mes"
                        tick={{ fontSize: 12, fill: "#6E86B2" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 12, fill: "#6E86B2" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(0,48,141,0.05)" }}
                        contentStyle={{
                          borderRadius: 10,
                          border: "none",
                          boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Bar
                        dataKey="registros"
                        name="Registros"
                        fill="#00308D"
                        radius={[8, 8, 0, 0]}
                        maxBarSize={48}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-5">
              <div className="tp-card h-100">
                <div className="tp-card-header">
                  <div className="tp-card-title">Suscripciones por estado</div>
                </div>
                <div className="tp-card-body" style={{ height: 300 }}>
                  {pieData.length === 0 ? (
                    <div className="empty-state">
                      <i className="bi bi-pie-chart" />
                      Aún no hay suscripciones
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={60}
                          outerRadius={95}
                          paddingAngle={3}
                        >
                          {pieData.map((d) => (
                            <Cell key={d.name} fill={d.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            borderRadius: 10,
                            border: "none",
                            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Legend
                          iconType="circle"
                          wrapperStyle={{ fontSize: 12 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Últimos registros ── */}
          <div className="tp-card">
            <div className="tp-card-header">
              <div className="tp-card-title">Últimos usuarios registrados</div>
              <button
                className="btn-tp-ghost"
                onClick={() => navigate("/usuarios")}
              >
                Ver todos <i className="bi bi-arrow-right" />
              </button>
            </div>
            {stats.latestUsers.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-people" />
                Aún no hay usuarios registrados
              </div>
            ) : (
              <div className="table-responsive">
                <table className="tp-table">
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Registro</th>
                      <th>Plan</th>
                      <th>Cuenta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.latestUsers.map((u) => (
                      <tr
                        key={u.id}
                        className="clickable"
                        onClick={() => navigate(`/usuarios/${u.id}`)}
                      >
                        <td>
                          <div className="cell-user">
                            <UserAvatar
                              imagePath={u.imagePath}
                              firstName={u.firstName}
                              lastName={u.lastName}
                              size="sm"
                            />
                            <div style={{ minWidth: 0 }}>
                              <div className="name">
                                {fullName(u.firstName, u.lastName, u.username)}
                              </div>
                              <div className="email">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>{formatDate(u.dateRegister)}</td>
                        <td>
                          <PlanBadge status={u.paymentStatus} />
                        </td>
                        <td>
                          <AccountBadge enabled={u.enabled} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
